import type { ReactNode } from 'react'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import rehypeSanitize from 'rehype-sanitize'

/**
 * Sanitized Markdown renderer for update bodies (and the composer live preview).
 * rehype-sanitize strips any raw HTML / unsafe attributes — authors write
 * Markdown only. Styling is scoped via the `updates-md` class (see updates.css).
 */
export function MarkdownView({ children }: { children: string }) {
  return (
    <div className="updates-md">
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        rehypePlugins={[rehypeSanitize]}
        components={{
          // Links open in a new tab; sanitize keeps only safe href schemes.
          a: ({ node: _node, ...props }) => (
            <a {...props} target="_blank" rel="noopener noreferrer" />
          ),
        }}
      >
        {children}
      </ReactMarkdown>
    </div>
  )
}

/**
 * Inline Markdown for list/row previews (cards, timeline, the header dropdown).
 * Same sanitized renderer as {@link MarkdownView}, but paragraphs are unwrapped
 * (no block `<div>`/`<p>` wrappers) so the emphasis (`**bold**`, `*italic*`,
 * `code`, links) renders in place without breaking the excerpt's CSS
 * line-clamp / single-line ellipsis truncation.
 */
export function InlineMarkdownView({ children }: { children: string }) {
  // Headings in a compact preview row shouldn't blow up the font size — render
  // them bold at the row's own size (like <strong>), not as large block headers.
  const heading = ({ children: c }: { children?: ReactNode }) => <strong>{c}</strong>
  return (
    <ReactMarkdown
      remarkPlugins={[remarkGfm]}
      rehypePlugins={[rehypeSanitize]}
      components={{
        // Unwrap paragraphs so text stays a direct child of the truncating box.
        p: ({ children: c }) => <>{c}</>,
        h1: heading,
        h2: heading,
        h3: heading,
        h4: heading,
        h5: heading,
        h6: heading,
        a: ({ node: _node, ...props }) => (
          <a {...props} target="_blank" rel="noopener noreferrer" />
        ),
      }}
    >
      {children}
    </ReactMarkdown>
  )
}
